<template>
    <div class="learningpath-description">
        <div class="learningpath-description__container">
            <span class="learningpath-description__label" v-html="$t('LearningPath.course_learningpath')"></span>
            <h1 class="learningpath-description__title" v-html="title"></h1>
            <Breadcrumb/>
            <LearningPathBannerInfo
                v-if="categories.length > 0 || levels.length > 0"
                :categories="categories"
                :levels="levels"
                :publishDate="publishDate"
                :expirationDate="expirationDate"
                :lastUpdatedDate="lastUpdatedDate"
            />
            <TotemAddToWatchlistButton
                v-if="showAddToFavourite"
                type="learningPath"
                :elementId="learningPathId"
            />
            <div class="learningpath-description__description -v-html" v-if="description"
                v-html="truncateDescription">
            </div>
            <div class="learningpath-description__read-more-container">
                <div class="learningpath-description__read-more" @click="openDescriptionModal" v-if="showReadMore">
                        <span v-html="$t('General.read_more')"></span>
                        <span class="icon-plus"></span>
                </div>
            </div>
            <div class="learningpath-description__actions">
                <LearningPathBannerSubscribeButton
                    v-if="hasAccessToLearningPath && !isLearningPathCompleted"
                    :isSubscribed="isSubscribed"
                    :learningPathId="learningPathId"
                />
                <LearningPathDescriptionAccessInfo
                    :hasCourseAccess="hasAccessToLearningPath"
                    :isLoggedIn="isLoggedIn"
                    :isSubscribed="isSubscribed"
                    :isCompleted="isLearningPathCompleted"
                />
            </div>
        </div>
        <LearningPathProgressBar
            :progress="progressPercentage"
            :isSubscribed="isSubscribed"
            :isBannerProgressBar="true"
            :isLearningPathProgress="true"
            :isCompleted="isLearningPathCompleted"
        />

        <modal
            name="learningpath-description"
            width="80%"
            :maxWidth="1200"
            height="auto"
            :adaptive="true"
            :scrollable="true"
        >
            <div class="learningpath-description__modal">
                <h1 class="learningpath-description__title">
                    <span v-html="$t('LearningPath.course_learningpath')"></span>
                    {{ title }}
                </h1>
                <span class="icon-clear learningpath-description__close-button" @click="closeModal"></span>
                <div class="learningpath-description__description -modal -v-html" v-html="description"></div>
            </div>
        </modal>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex'
    import LearningPathBannerInfo from 'components/learningpathpage/banner/LearningPathBannerInfo'
    import LearningPathProgressBar from 'components/learningpathpage/banner/LearningPathProgressBar'
    import LearningPathBannerSubscribeButton from 'components/learningpathpage/banner/LearningPathBannerSubscribeButton'
    import LearningPathDescriptionAccessInfo from 'components/learningpathpage/banner/LearningPathDescriptionAccessInfo'
    import Breadcrumb from 'components/breadcrumb/breadcrumb'

    export default {
        components: {
            LearningPathBannerInfo,
            LearningPathProgressBar,
            LearningPathBannerSubscribeButton,
            LearningPathDescriptionAccessInfo,
            Breadcrumb
        },
        props: {
            learningPathId: [ Number, String ],
            title: String,
            description: String,
            categories: Array,
            levels: Array,
            publishDate: String,
            expirationDate: String,
            lastUpdatedDate: String,
            progressPercentage: Number,
            isSubscribed: Boolean,
            isLearningPathCompleted: Boolean,
            hasAccessToLearningPath: Boolean
        },
        data () {
            return {
                learningPathToNewWatchlist: null,
                desktopDescLength: 650,
                mobileDescLength: 200
            }
        },
        computed: {
            ...mapGetters({
                isLoggedIn: 'isLoggedIn'
            }),
            showAddToFavourite () {
                return this.isLoggedIn
            },
            truncateDescription () {
                let length = this.isDesktopView ? this.desktopDescLength : this.mobileDescLength
                if (this.description.length < length) {
                    return this.description
                } else {
                    return this.description.substring(0, length) + '...'
                }
            },
            showReadMore () {
                return this.isDesktopView ? this.description.length > this.desktopDescLength : this.description.length > this.mobileDescLength
            }
        },
        methods: {
            openDescriptionModal () {
                this.$modal.show('learningpath-description')
            },
            async addToNewWatchlist (name, learningPathId) {
                this.learningPathToNewWatchlist = { name: name, learningPathId: learningPathId, modalIsOpen: false }
                this.$modal.hide('add-to-new-watchlist')
                await this.$store.dispatch('User/addLearningPathToNewList', { learningPathId, name })
            },
            closeModal () {
                this.$modal.hide('learningpath-description')
            }
        }
    }
</script>

<style lang="scss" scoped>

    .learningpath-description {
        position: relative;
        flex-grow: 1;

        &__container {
            padding: 30px 20px 30px 20px;

            [class*="desktop"] & {
                font-size: 2.8rem;
                padding: 30px 50px 30px 50px;
            }
        }

        &__label {
            font-size: 1.8rem;
            display: block;
            [class*="desktop"] & {
                margin: 20px 0 5px;
            }
        }

        &__title {
            font-family: Quicksand-Bold, sans-serif;
            font-size: 2rem;
            padding: 0 0 6px;

            [class*="desktop"] & {
                font-size: 2.4rem;
            }
        }

        &__description {
            font-size: 1.5rem;

            [class*="desktop"] & {
                font-size: 1.7rem;
            }

            &.-modal {
                [class*="desktop"] & {
                    font-size: 1.7rem;
                    line-height: 2.4rem;
                }
                font-size: 1.5rem;
            }
        }

        &__actions {
            display: flex;
            margin: 30px 0 0;
            gap: 15px;
            align-items: center;
            justify-content: center;

            [class*="mobile"] & {
                flex-direction: column;
                margin: 20px 0 0;
            }
        }

        &__read-more-container {
            display: flex;

            [class*="mobile"] & {
                margin-top: 20px;
                padding: 0 25px;
            }
        }

        &__read-more {
            font-size: 1.9rem;
            font-family: Nunito-Bold, sans-serif;
            flex: 1;
            text-align: center;
            align-items: center;
            display: flex;
            justify-content: center;

            &:hover {
                cursor: pointer;
            }
            [class*="desktop"] & {
                align-self: flex-end;
            }
            [class*="mobile"] & {
                margin: 0 auto;
            }
        }

        .icon-plus {
            font-size: 1.8rem;
        }

        .icon-info {
            position: absolute;
            top: 75px;
            right: 32px;
            font-size: 2.5rem;

            [class*="desktop"] & {
                top: 80px;
            }
        }

        &__modal {
            padding: 40px 50px;
        }

        &__close-button {
            position: absolute;
            right: 15px;
            top: 15px;
            font-size: 4rem;
            color: $black;
            &:hover {
                opacity: 0.7;
            }
        }

        &__recommended-pace {
            font-size: 1.5rem;

            [class*="desktop"] & {
                font-size: 1.9rem;
                margin: 15px 0px;
            }
        }

        &__recommended-pace-label {
            font-family: Quicksand-Bold;
        }
    }
</style>
